/**
 * SEOHead Component - Unified SEO metadata management
 * 
 * This component provides comprehensive SEO optimization:
 * - Multi-language support with hreflang tags
 * - Open Graph and Twitter Cards for social sharing
 * - Canonical URLs for duplicate content prevention
 * - Structured metadata following best practices
 * 
 * MULTILINGUAL SEO STRATEGY:
 * - All pages use Spanish (/es/*) as canonical URL (aligns with .es domain)
 * - Other languages marked with noindex to consolidate SEO authority
 * - Hreflang tags guide users to correct language version
 * - x-default points to Spanish as primary version
 * - This prevents duplicate content issues and clarifies primary content to Google
 * 
 * Usage:
 * - Use the useSEO hook for most cases (recommended)
 * - Or use this component directly when you need custom metadata
 * 
 * Best Practices:
 * - Always provide unique titles and descriptions per page
 * - Use descriptive, action-oriented language
 * - Keep titles under 60 characters
 * - Keep descriptions between 150-160 characters
 * - Provide relevant keywords (3-8 keywords recommended)
 * - Use high-quality images (1200x630px) for ogImage
 */

import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { 
  SUPPORTED_LANGUAGES, 
  DEFAULT_LANGUAGE, 
  LANGUAGE_METADATA,
  type SupportedLanguage,
  extractLanguageFromPath,
  removeLanguagePrefix
} from '@config/i18n.config';
import { 
  SEO_DOMAIN, 
  DEFAULT_OG_IMAGE, 
  DEFAULT_CANONICAL_LANG,
  PRIMARY_INDEX_LANGUAGES 
} from '@config/seo.config';

export interface SEOHeadProps {
  /** Page title (appears in browser tab and search results) */
  title: string;
  
  /** Page description (appears in search results) */
  description: string;
  
  /** Comma-separated keywords for SEO */
  keywords?: string;
  
  /** Open Graph image URL (for social sharing preview) */
  ogImage?: string;
  
  /** Open Graph type (website, article, product, etc.) */
  ogType?: 'website' | 'article' | 'product' | 'profile';
  
  /** If true, prevents search engines from indexing this page */
  noindex?: boolean;
  
  /** Custom canonical URL (optional, auto-generated if not provided) */
  canonicalUrl?: string;
  
  /** Article-specific: publication date (ISO 8601 format) */
  publishedTime?: string;
  
  /** Article-specific: last modification date (ISO 8601 format) */
  modifiedTime?: string;
  
  /** Article-specific: author name(s) */
  author?: string | string[];
  
  /** Article-specific: article section/category */
  section?: string;
  
  /** Article-specific: article tags */
  tags?: string[];
}

export function SEOHead({
  title,
  description,
  keywords,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  noindex = false,
  canonicalUrl,
  publishedTime,
  modifiedTime,
  author,
  section,
  tags,
}: SEOHeadProps) {
  const location = useLocation();
  const currentLang = extractLanguageFromPath(location.pathname) || DEFAULT_LANGUAGE;
  const basePath = removeLanguagePrefix(location.pathname);
  
  // Build alternate URLs for all supported languages
  const alternateUrls = SUPPORTED_LANGUAGES.reduce((acc, lang) => {
    acc[lang] = `${SEO_DOMAIN}/${lang}${basePath}`;
    return acc;
  }, {} as Record<SupportedLanguage, string>);

  // Current page URL with language prefix
  const currentUrl = alternateUrls[currentLang];
  
  // Canonical URL: Always point to the canonical language version (Spanish)
  // This consolidates SEO authority and prevents duplicate content issues
  const canonicalLanguageUrl = `${SEO_DOMAIN}/${DEFAULT_CANONICAL_LANG}${basePath}`;
  const canonical = canonicalUrl || canonicalLanguageUrl;
  
  // Determine if this language should be indexed
  // Primary languages get indexed, secondary languages use noindex but remain in hreflang
  const shouldIndex = PRIMARY_INDEX_LANGUAGES.includes(currentLang as any) && !noindex;
  
  // Get language metadata
  const langMeta = LANGUAGE_METADATA[currentLang];
  
  // Format authors for article:author tags
  const authors = Array.isArray(author) ? author : author ? [author] : [];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={langMeta.htmlLang} dir={langMeta.dir} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      
      {/* Prevent Google auto-translation (we provide proper translations via hreflang) */}
      <meta name="google" content="notranslate" />
      
      {/* Robots meta tags - Secondary languages use noindex to consolidate SEO */}
      {!shouldIndex ? (
        <>
          <meta name="robots" content="noindex,follow" />
          <meta name="googlebot" content="noindex,follow" />
        </>
      ) : (
        <>
          <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
          <meta name="googlebot" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
        </>
      )}

      {/* Canonical URL - Always points to Spanish version for SEO consolidation */}
      <link rel="canonical" href={canonical} />

      {/* Hreflang Tags for Multi-language SEO */}
      {SUPPORTED_LANGUAGES.map((lang) => (
        <link
          key={`hreflang-${lang}`}
          rel="alternate"
          hrefLang={lang}
          href={alternateUrls[lang]}
        />
      ))}
      {/* x-default hreflang points to canonical language (Spanish for .es domain) */}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={alternateUrls[DEFAULT_CANONICAL_LANG]}
      />

      {/* Open Graph Meta Tags - General */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:locale" content={langMeta.ogLocale} />
      {SUPPORTED_LANGUAGES.filter(lang => lang !== currentLang).map((lang) => (
        <meta
          key={`og-locale-alt-${lang}`}
          property="og:locale:alternate"
          content={LANGUAGE_METADATA[lang].ogLocale}
        />
      ))}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Finanzly" />

      {/* Open Graph Meta Tags - Article Specific */}
      {ogType === 'article' && (
        <>
          {publishedTime && (
            <meta property="article:published_time" content={publishedTime} />
          )}
          {modifiedTime && (
            <meta property="article:modified_time" content={modifiedTime} />
          )}
          {authors.map((authorName, index) => (
            <meta key={`author-${index}`} property="article:author" content={authorName} />
          ))}
          {section && <meta property="article:section" content={section} />}
          {tags?.map((tag, index) => (
            <meta key={`tag-${index}`} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:site" content="@finanzly" />
      <meta name="twitter:creator" content="@finanzly" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content={authors.length > 0 ? authors.join(', ') : 'Finanzly'} />
      <meta name="generator" content="Finanzly Platform" />
      <meta name="application-name" content="Finanzly" />
      <meta name="apple-mobile-web-app-title" content="Finanzly" />
      <meta name="theme-color" content="#10b981" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />

      {/* Additional SEO Meta Tags */}
      <meta name="language" content={langMeta.htmlLang} />
      <meta httpEquiv="content-language" content={langMeta.htmlLang} />
    </Helmet>
  );
}
